/**
 * AssessmentForm component for the multi-step assessment process
 */
import React, { useState, useEffect } from 'react';
import { useAssessmentContext } from '../../context/AssessmentContext';
import QuestionRenderer from '../QuestionRenderer';
import ProgressIndicator from '../ProgressIndicator';
import { Button } from '@/components/ui/button';
import questions from '../../data/questions';
import * as apiService from '../../services/apiService';

interface AssessmentFormProps {
  onComplete: (assessmentId: string) => void;
}

const AssessmentForm: React.FC<AssessmentFormProps> = ({ onComplete }) => {
  const { state, dispatch } = useAssessmentContext();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Get current question
  const currentQuestion = questions[currentIndex];
  
  // Check if current question is answered
  const isCurrentQuestionAnswered = Boolean(state.responses[currentQuestion?.id]);
  
  // Check if we're on the last question
  const isLastQuestion = currentIndex === questions.length - 1;
  
  // Update context when current question changes
  useEffect(() => {
    dispatch({ type: 'SET_CURRENT_QUESTION', payload: currentIndex });
  }, [currentIndex, dispatch]);
  
  // Handle question response
  const handleAnswer = (questionId: string, value: number) => {
    dispatch({ 
      type: 'SET_RESPONSE', 
      payload: { questionId, value } 
    });
  };
  
  // Handle navigation
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };
  
  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };
  
  // Handle submission
  const handleSubmit = async () => {
    if (!isCurrentQuestionAnswered) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Create assessment if it doesn't exist yet
      let assessmentId = state.assessment?.id;
      
      if (!assessmentId && state.user?.id) {
        const assessment = await apiService.createAssessment(state.user.id);
        assessmentId = assessment.id;
        
        dispatch({
          type: 'SET_ASSESSMENT',
          payload: assessment
        });
      }
      
      if (!assessmentId) {
        throw new Error('Unable to create assessment');
      }
      
      // Save responses
      await apiService.saveResponses(assessmentId, state.responses);
      
      // Calculate and save results
      const updatedAssessment = await apiService.saveResults(assessmentId, state.user.id, state.responses);
      
      // Update context with completed assessment
      dispatch({
        type: 'SET_ASSESSMENT',
        payload: updatedAssessment
      });
      
      // Move to results page
      onComplete(assessmentId);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setError('An error occurred while submitting your assessment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // If no current question, show error
  if (!currentQuestion) {
    return (
      <div className="text-center p-8">
        <p className="text-red-500">Error loading assessment questions.</p>
      </div>
    );
  }
  
  return (
    <div className="w-full max-w-2xl mx-auto bg-white p-6 rounded-lg shadow-md">
      <ProgressIndicator 
        currentStep={currentIndex + 1} 
        totalSteps={questions.length} 
      />
      
      <div className="my-8">
        <QuestionRenderer 
          question={currentQuestion} 
          onAnswer={handleAnswer} 
        />
      </div>
      
      {error && (
        <div className="bg-red-50 p-3 rounded border border-red-200 mb-4">
          <p className="text-red-500">{error}</p>
        </div>
      )}
      
      <div className="flex justify-between mt-8">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentIndex === 0 || isSubmitting}
        >
          Previous
        </Button>
        
        {isLastQuestion ? (
          <Button
            onClick={handleSubmit}
            disabled={!isCurrentQuestionAnswered || isSubmitting}
            className="bg-deepBlue hover:bg-deepBlue-light text-white"
          >
            {isSubmitting ? 'Submitting...' : 'Complete Assessment'}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            disabled={!isCurrentQuestionAnswered || isSubmitting}
            className="bg-deepBlue hover:bg-deepBlue-light text-white"
          >
            Next Question
          </Button>
        )}
      </div>
    </div>
  );
};

export default AssessmentForm;
